import React, { useEffect, useState } from "react";
import Profile from "./Profile"; // Import the Profile component
import GeneralSettings from "./GeneralSettings"; // Import GeneralSettings component
import PrivacySettings from "./PrivacySettings";
import FlightSearch from "./FlightSearch";
import ContactUs from "./ContactUs";
import { useGlobalLoader } from "../../../src/contexts/GlobalLoaderContext";
import { useNavigate, useSearchParams } from "react-router-dom";

const Index: React.FC = () => {
  const { showLoader, hideLoader } = useGlobalLoader();
  const [searchParams, setSearchParams] = useSearchParams();
  const getInitialSection = () => {
    const section = searchParams.get("section");
    return section ?? "profile";
  };
  const [activeComponent, setActiveComponent] =
    useState<string>(getInitialSection()); // State to track active component
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const navigate = useNavigate(); // Initialize the navigate function

  useEffect(() => {
    showLoader();
    setTimeout(() => {
      hideLoader();
    }, 1000); // Shows loader for 1 second on initial mount
  }, []);

  const menuItems = [
    {
      id: "profile",
      label: "My Account",
      description: "Make Changes To Your Account",
      icon: "st-user",
    },
    {
      id: "privacy",
      label: "Privacy",
      description: "Keeping Your Data Safe and Secure",
      icon: "st-shield",
    },
    {
      id: "flightSearch",
      label: "Flight Search",
      description: "Find Flights That Fit You",
      icon: "st-flight",
    },
  ];

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const handleMenuItemClick = (id: string) => {
    setActiveComponent(id); // Update active component
    if (id === "profile") {
      setSearchParams({}, { replace: true });
    } else {
      setSearchParams({ section: id }, { replace: true });
    }
    setIsSidebarOpen(false); // Close the sidebar when a menu item is clicked
  };

  useEffect(() => {
    const section = searchParams.get("section");
    const allowableSections = [
      "profile",
      "privacy",
      "flightSearch",
      "contactUs",
    ];

    if (
      section &&
      allowableSections.includes(section) &&
      section !== activeComponent
    ) {
      setActiveComponent(section);
    } else if (!section && activeComponent !== "profile") {
      setActiveComponent("profile");
    }
  }, [searchParams, activeComponent]);

  return (
    <div>
      {/* Title row — toggle sits inline with the title on mobile, no overlap */}
      {activeComponent !== "contactUs" && (
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={toggleSidebar}
            aria-controls="default-sidebar"
            type="button"
            className="sm:hidden inline-flex items-center p-2 text-sm text-gray-500 rounded-lg focus:outline-none bg-white shadow"
          >
            <span className="sr-only">Open sidebar</span>
            <svg
              className="w-5 h-5"
              aria-hidden="true"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                clipRule="evenodd"
                fillRule="evenodd"
                d="M2 4.75A.75.75 0 012.75 4h14.5a.75.75 0 010 1.5H2.75A.75.75 0 012 4.75zm0 10.5a.75.75 0 01.75-.75h7.5a.75.75 0 010 1.5h-7.5a.75.75 0 01-.75-.75zM2 10a.75.75 0 01.75-.75h14.5a.75.75 0 010 1.5H2.75A.75.75 0 012 10z"
              />
            </svg>
          </button>
          <h1 className="text-2xl font-bold text-grease-100">Settings</h1>
        </div>
      )}

      <div className="flex gap-6">
        {activeComponent !== "contactUs" && (
          <>
            {/* Mobile backdrop */}
            {isSidebarOpen && (
              <div
                className="bg-gray-900/50 fixed inset-0 z-30 sm:hidden"
                onClick={toggleSidebar}
              />
            )}

            {/* Sidebar */}
            <aside
              className={`bg-white rounded-xl w-[280px] flex-shrink-0 sm:block ${
                isSidebarOpen
                  ? "fixed inset-y-0 left-0 z-40 block"
                  : "hidden sm:block"
              }`}
            >
              {/* Split padding (not the `p-5` shorthand) so the top override actually
                  applies: when open as a mobile overlay, push content below the global
                  floating hamburger (fixed top-4 left-4 z-50). */}
              <div
                className={`px-5 pb-5 ${
                  isSidebarOpen ? "pt-20 sm:pt-5" : "pt-5"
                }`}
              >
                <ul className="space-y-1">
                  {menuItems.map((item) => (
                    <li key={item.id}>
                      <a
                        href="#"
                        className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                          activeComponent === item.id
                            ? "bg-[#FFBC001A] text-[#FFBC00]"
                            : "text-grease-100 hover:bg-gray-50"
                        }`}
                        onClick={() => handleMenuItemClick(item.id)}
                      >
                        <i
                          className={`${item.icon} text-lg ${activeComponent === item.id ? "text-[#FFBC00]" : "text-lightgray-150"}`}
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium">{item.label}</p>
                          {item.description && (
                            <p className="text-[10px] text-gray-500 mt-0.5 truncate">
                              {item.description}
                            </p>
                          )}
                        </div>
                        <i className="st-arrow-right text-xs text-gray-300" />
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </aside>
          </>
        )}

        {/* Main Content */}
        <div className="flex-1 min-w-0">
          {activeComponent === "profile" && <Profile />}
          {activeComponent === "privacy" && <PrivacySettings />}
          {activeComponent === "general" && <GeneralSettings />}
          {activeComponent === "flightSearch" && <FlightSearch />}
          {activeComponent === "contactUs" && <ContactUs />}
        </div>
      </div>
    </div>
  );
};

export default Index;
